import React, { useState, useEffect } from 'react';
import { conferenceRoomApi, amenityApi, locationApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import './AddRoomModal.css';

function AddRoomModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    roomName: '',
    roomType: '',
    location: '',
    tower: '',
    floor: '',
    capacity: '',
    amenities: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState('');
  const [showCustomRoomType, setShowCustomRoomType] = useState(false);
  const [customRoomType, setCustomRoomType] = useState('');
  const [availableAmenities, setAvailableAmenities] = useState([]);

  // Location Master dropdown
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Fetch active amenities and locations when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAmenities();
      loadLocations();
    }
  }, [isOpen]);

  const loadAmenities = async () => {
    try {
      const data = await amenityApi.getActive();
      const amenitiesList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setAvailableAmenities(amenitiesList);
    } catch (error) {
      console.error('Failed to load amenities:', error);
      // Fallback to default amenities if API fails
      setAvailableAmenities([
        { id: 1, name: 'Projector', icon: '📽️' },
        { id: 2, name: 'Whiteboard', icon: '🖊️' },
        { id: 3, name: 'Video Conference', icon: '🎥' },
        { id: 4, name: 'WiFi', icon: '📶' },
        { id: 5, name: 'TV Display', icon: '🖥️' },
        { id: 6, name: 'Phone', icon: '📞' },
        { id: 7, name: 'Air Conditioning', icon: '🌡️' },
        { id: 8, name: 'Natural Light', icon: '🪟' }
      ]);
    }
  };

  const loadLocations = async () => {
    try {
      setIsLoadingLocations(true);
      const response = await locationApi.getAllActiveLocationNames();
      const locationNames = response?.data || response || [];
      setAvailableLocations(locationNames);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setAvailableLocations([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleLocationChange = (value) => {
    if (value === 'Others') {
      setShowCustomLocation(true);
      setFormData(prev => ({ ...prev, location: '' }));
    } else {
      setShowCustomLocation(false);
      setCustomLocation('');
      setFormData(prev => ({ ...prev, location: value }));
    }
    if (errors.location) {
      setErrors(prev => ({ ...prev, location: null }));
    }
  };

  const handleRoomTypeChange = (value) => {
    if (value === 'Other') {
      setShowCustomRoomType(true);
      setFormData(prev => ({ ...prev, roomType: '' }));
    } else {
      setShowCustomRoomType(false);
      setCustomRoomType('');
      setFormData(prev => ({ ...prev, roomType: value }));
    }
    if (errors.roomType) {
      setErrors(prev => ({ ...prev, roomType: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.roomName.trim()) {
      newErrors.roomName = 'Room name is required';
    }

    if (showCustomRoomType) {
      if (!customRoomType.trim()) {
        newErrors.roomType = 'Custom room type is required';
      }
    } else {
      if (!formData.roomType) {
        newErrors.roomType = 'Room type is required';
      }
    }

    if (showCustomLocation) {
      if (!customLocation.trim()) {
        newErrors.location = 'Custom location is required';
      }
    } else {
      if (!formData.location.trim()) {
        newErrors.location = 'Location is required';
      }
    }

    if (!formData.tower.trim()) {
      newErrors.tower = 'Tower is required';
    }

    if (!formData.floor.trim()) {
      newErrors.floor = 'Floor is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      await conferenceRoomApi.add({
        roomName: formData.roomName,
        roomType: showCustomRoomType ? customRoomType : formData.roomType,
        location: showCustomLocation ? customLocation : formData.location,
        tower: formData.tower,
        floor: formData.floor,
        capacity: formData.capacity ? parseInt(formData.capacity): null,
        amenities: formData.amenities.length > 0 ? formData.amenities.join(',') : null,
      });

      toast.success('Workspace added successfully!', {
        position: "top-right",
        autoClose: 3000,
      });

      resetForm();
      onSuccess && onSuccess();
      onClose();

    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to add room';
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      roomName: '',
      roomType: '',
      location: '',
      tower: '',
      floor: '',
      capacity: '',
      amenities: []
    });
    setErrors({});
    setShowCustomLocation(false);
    setCustomLocation('');
    setShowCustomRoomType(false);
    setCustomRoomType('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="add-room-modal-overlay" onClick={handleClose}>
      <div className="add-room-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-room-modal-header">
          <h2>Add Workspace</h2>
          <button className="add-room-modal-close" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="add-room-form">
          {/* ROOM NAME */}
          <div className="form-group">
            <label htmlFor="roomName">
              Room Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="roomName"
              value={formData.roomName}
              onChange={(e) => handleChange('roomName', e.target.value)}
              placeholder="e.g., Executive Board Room, Meeting Room A"
              className={errors.roomName ? 'error' : ''}
            />
            {errors.roomName && (
              <span className="error-message">{errors.roomName}</span>
            )}
          </div>

          {/* ROOM TYPE */}
          <div className="form-group">
            <label htmlFor="roomType">
              Room Type <span className="required">*</span>
            </label>
            <select
              id="roomType"
              value={showCustomRoomType ? 'Other' : formData.roomType}
              onChange={(e) => handleRoomTypeChange(e.target.value)}
              className={errors.roomType ? 'error' : ''}
            >
              <option value="">Select room type</option>
              <option value="Meeting Room">Meeting Room</option>
              <option value="Conference Room">Workspace</option>
              <option value="Medical Room">Medical Room</option>
              <option value="Board Room">Board Room</option>
              <option value="Training Room">Training Room</option>
              <option value="Other">Other</option>
            </select>
            {errors.roomType && !showCustomRoomType && (
              <span className="error-message">{errors.roomType}</span>
            )}
          </div>

          {/* CUSTOM ROOM TYPE INPUT */}
          {showCustomRoomType && (
            <div className="form-group">
              <label htmlFor="customRoomType">
                Enter Room Type <span className="required">*</span>
              </label>
              <input
                type="text"
                id="customRoomType"
                value={customRoomType}
                onChange={(e) => setCustomRoomType(e.target.value)}
                placeholder="Enter custom room type"
                className={errors.roomType ? 'error' : ''}
              />
              {errors.roomType && (
                <span className="error-message">{errors.roomType}</span>
              )}
            </div>
          )}

          {/* LOCATION */}
          <div className="form-group">
            <label htmlFor="location">
              Location <span className="required">*</span>
            </label>
            <select
              id="location"
              value={showCustomLocation ? 'Others' : formData.location}
              onChange={(e) => handleLocationChange(e.target.value)}
              className={errors.location ? 'error' : ''}
              disabled={isLoadingLocations}
            >
              <option value="">
                {isLoadingLocations ? 'Loading locations...' : 'Select location'}
              </option>
              {availableLocations.map(locationName => (
                <option key={locationName} value={locationName}>
                  {locationName}
                </option>
              ))}
              <option value="Others">Others (Custom)</option>
            </select>
            {availableLocations.length === 0 && !isLoadingLocations && !showCustomLocation && (
              <small style={{ color: '#e74c3c', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                No active locations found. You can enter a custom location.
              </small>
            )}
            {errors.location && !showCustomLocation && (
              <span className="error-message">{errors.location}</span>
            )}
          </div>

          {/* CUSTOM LOCATION INPUT */}
          {showCustomLocation && (
            <div className="form-group">
              <label htmlFor="customLocation">
                Enter Location <span className="required">*</span>
              </label>
              <input
                type="text"
                id="customLocation"
                value={customLocation}
                onChange={(e) => setCustomLocation(e.target.value)}
                placeholder="Enter custom location"
                className={errors.location ? 'error' : ''}
              />
              {errors.location && (
                <span className="error-message">{errors.location}</span>
              )}
            </div>
          )}

          {/* TOWER */}
          <div className="form-group">
            <label htmlFor="tower">
              Tower <span className="required">*</span>
            </label>
            <input
              type="text"
              id="tower"
              value={formData.tower}
              onChange={(e) => handleChange('tower', e.target.value)}
              placeholder="e.g., Tower A, Tower B, Main Building"
              className={errors.tower ? 'error' : ''}
            />
            {errors.tower && (
              <span className="error-message">{errors.tower}</span>
            )}
          </div>

          {/* FLOOR */}
          <div className="form-group">
            <label htmlFor="floor">
              Floor <span className="required">*</span>
            </label>
            <input
              type="text"
              id="floor"
              value={formData.floor}
              onChange={(e) => handleChange('floor', e.target.value)}
              placeholder="e.g., 1st Floor, Ground Floor, 5th Floor"
              className={errors.floor ? 'error' : ''}
            />
            {errors.floor && (
              <span className="error-message">{errors.floor}</span>
            )}
          </div>
          {/* CAPACITY */}
          <div className = "form-group">
            <label htmlFor="capacity">Capacity</label>
            <input
            type="number"
            id ="capacity"
            value={formData.capacity}
            onChange={(e) => handleChange('capacity' , e.target.value)}
            placeholder="e.g., 10, 20, 50"
            min="1"
            className={errors.capacity ? 'error' : ''}
            />
            {errors.capacity && (
              <span className="error-message">{errors.capacity}</span>
            )}
          </div>

          {/* AMENITIES */}
          <div className="form-group">
            <label>Amenities</label>
            {availableAmenities.length === 0 ? (
              <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                Loading amenities...
              </p>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '10px',
                marginTop: '8px'
              }}>
                {availableAmenities.map(amenity => (
                  <label key={amenity.id || amenity.name} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px',
                    border: formData.amenities.includes(amenity.name) ? '2px solid #4CAF50' : '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: formData.amenities.includes(amenity.name) ? '#e8f5e9' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.amenities.includes(amenity.name)}
                      onChange={(e) => {
                        const newAmenities = e.target.checked
                          ? [...formData.amenities, amenity.name]
                          : formData.amenities.filter(a => a !== amenity.name);
                        handleChange('amenities', newAmenities);
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    <span style={{ fontSize: '18px', marginRight: '6px' }}>{amenity.icon || '✓'}</span>
                    <span style={{ fontSize: '14px', color: '#1a202c', fontWeight: '500' }}>{amenity.name}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="helper-text" style={{ marginTop: '8px' }}>
              Select all amenities available in this room
            </p>
          </div>

          {/* INFO MESSAGE */}
          <div className="info-message">
            <span className="info-icon">ℹ️</span>
            <span>Once added, the room will be available for booking immediately.</span>
          </div>

          {/* FORM ACTIONS */}
          <div className="add-room-form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Room'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddRoomModal;
